/**
 * AppLayout Component
 * Main application layout with left sidebar navigation and content area
 */

import { ReactNode, useState, useEffect, useCallback } from 'react';
import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { WorkspaceSidebar } from './WorkspaceSidebar';
import { PendingRedemptionBanner } from './PendingRedemptionBanner';
import { useTenantContext } from '../../contexts/TenantContext';
import {
  getSidebarCollapsedState,
  setSidebarCollapsedState,
  getSidebarWidth,
  setSidebarWidth,
} from '../../utils/sidebarStateUtils';

export interface AppLayoutProps {
  children: ReactNode;
  header?: ReactNode;
}

const useStyles = makeStyles({
  layout: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    overflow: 'hidden',
  },
  topHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding(0, '32px'),
    backgroundColor: tokens.colorNeutralBackground4,
    boxShadow: tokens.shadow4, // Subtle drop shadow for depth
    minHeight: '46px', // Compact header height
  },
  mainArea: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  contentArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

/**
 * Main application layout with optional header, sidebar, and content area
 *
 * @param children - Content to render in the main area
 * @param header - Optional header content to display above sidebar and content
 */
export function AppLayout({ children, header }: AppLayoutProps) {
  const styles = useStyles();
  const { pendingTenants } = useTenantContext();

  // Sidebar collapsed state management
  const [isCollapsed, setIsCollapsed] = useState(() => getSidebarCollapsedState());

  // Sidebar width state management
  const [sidebarWidth, setSidebarWidthState] = useState(() => getSidebarWidth());

  // Initialize collapsed state from localStorage
  useEffect(() => {
    setIsCollapsed(getSidebarCollapsedState());
  }, []);

  // Initialize width state from localStorage
  useEffect(() => {
    setSidebarWidthState(getSidebarWidth());
  }, []);

  // Toggle sidebar collapsed state
  const handleToggle = () => {
    setIsCollapsed((prev) => {
      const newState = !prev;
      setSidebarCollapsedState(newState);
      return newState;
    });
  };

  // Handle sidebar width change
  const handleWidthChange = useCallback((newWidth: number) => {
    setSidebarWidthState(newWidth);
    setSidebarWidth(newWidth);
  }, []);

  return (
    <div className={styles.layout}>
      {header && (
        <header className={styles.topHeader}>
          {header}
        </header>
      )}
      <PendingRedemptionBanner tenants={pendingTenants} />
      <div className={styles.mainArea}>
        <WorkspaceSidebar
          isCollapsed={isCollapsed}
          onToggle={handleToggle}
          width={sidebarWidth}
          onWidthChange={handleWidthChange}
        />
        <main role="main" className={styles.contentArea}>
          {children}
        </main>
      </div>
    </div>
  );
}
